import { useEffect, useRef, useState } from "react";

interface SSEOptions {
  /** Polling fallback interval used when SSE is unavailable / failing. */
  fallbackIntervalMs: number;
  /** Reconnect delay if the stream drops. */
  reconnectMs?: number;
  /** Pause when document.hidden. */
  pauseWhenHidden?: boolean;
  /**
   * Treat the stream as dead if no frame arrives for this long.
   *
   * Recovery used to hang entirely off `onerror`, but a connection can die
   * without ever firing it — a half-open socket, a proxy dropping a long-lived
   * response, a laptop changing networks. Observed in production: a tab sat at
   * "API CONNECTED" while its newest fix aged past 28 minutes, and a freshly
   * opened tab beside it was current, so neither the server nor the data was at
   * fault. Nothing reconnected and the fallback poll never started, because
   * both are only reachable through the error path.
   *
   * The server ticks this stream every 2s, so 30s is far past any jitter.
   */
  stallTimeoutMs?: number;
  /** Called as soon as the stream errors, before the reconnect timer starts.
   *  EventSource gives no HTTP status on failure, so a dead/expired access
   *  token looks identical to a network blip — this is the hook's chance to
   *  proactively refresh the token rather than reconnecting blind and
   *  waiting on the fallback poll's own 401 handling to eventually catch up,
   *  which is what let a stream sit dead-and-retrying past its token expiry. */
  onConnectionError?: () => void;
}

export interface SSEResult<T> {
  data: T | undefined;
  error: string | null;
  lastUpdatedAt: number | null;
  /** True while we are receiving events from EventSource. */
  streaming: boolean;
}

/**
 * Hook that consumes an SSE endpoint with a JSON `data: …` frame per
 * update, falling back to plain polling if EventSource is unavailable
 * or the stream fails. The shape mirrors usePollingQuery so callers can
 * swap with a single import change.
 *
 * `streamUrl` is queried with the access_token query-string appended so
 * browser EventSource (which can't set custom headers) authenticates.
 * The `fallback` fetcher is used to poll the same data when the stream
 * is dead.
 */
export function useSSEQuery<T>(
  streamUrl: string,
  accessTokenGetter: () => string | null,
  fallback: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
  options: SSEOptions
): SSEResult<T> {
  const {
    fallbackIntervalMs,
    reconnectMs = 5_000,
    pauseWhenHidden = true,
    onConnectionError,
    stallTimeoutMs = 30_000,
  } = options;
  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [streaming, setStreaming] = useState(false);

  const cancelledRef = useRef(false);
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  const onConnectionErrorRef = useRef(onConnectionError);
  onConnectionErrorRef.current = onConnectionError;

  useEffect(() => {
    cancelledRef.current = false;
    let es: EventSource | null = null;
    let pollId: number | undefined;
    let reconnectId: number | undefined;
    let watchdogId: number | undefined;
    let lastFrameAt = Date.now();

    const startPolling = () => {
      if (pollId != null) return;
      const tick = () => {
        if (cancelledRef.current) return;
        void fallbackRef.current()
          .then((v) => {
            if (cancelledRef.current) return;
            setData(v);
            setLastUpdatedAt(Date.now());
            setError(null);
          })
          .catch((err) => {
            if (cancelledRef.current) return;
            setError(err instanceof Error ? err.message : "Fetch failed");
          });
      };
      tick();
      pollId = window.setInterval(tick, fallbackIntervalMs);
    };

    const stopPolling = () => {
      if (pollId != null) {
        window.clearInterval(pollId);
        pollId = undefined;
      }
    };

    const stopWatchdog = () => {
      if (watchdogId != null) {
        window.clearInterval(watchdogId);
        watchdogId = undefined;
      }
    };

    /**
     * One place to give up on the current stream, whether EventSource told us
     * it broke or the watchdog noticed it went quiet. Falls back to polling
     * immediately so the screen keeps moving, then retries the stream.
     */
    const failStream = (reason: string) => {
      if (cancelledRef.current) return;
      stopWatchdog();
      setStreaming(false);
      es?.close();
      es = null;
      if (reason === "stall") {
        console.warn(`[sse] no frame for ${Math.round(stallTimeoutMs / 1000)}s; falling back to polling`);
      }
      // EventSource can't tell us this was a 401, so refresh the token rather
      // than reconnecting blind.
      onConnectionErrorRef.current?.();
      startPolling();
      if (reconnectId != null) window.clearTimeout(reconnectId);
      reconnectId = window.setTimeout(() => {
        if (cancelledRef.current) return;
        startStream();
      }, reconnectMs);
    };

    const startWatchdog = () => {
      stopWatchdog();
      lastFrameAt = Date.now();
      watchdogId = window.setInterval(() => {
        if (cancelledRef.current || es == null) return;
        if (Date.now() - lastFrameAt > stallTimeoutMs) failStream("stall");
      }, Math.max(1_000, Math.floor(stallTimeoutMs / 5)));
    };

    const startStream = () => {
      const token = accessTokenGetter();
      if (typeof window === "undefined" || typeof window.EventSource === "undefined" || !token) {
        startPolling();
        return;
      }
      // Close any existing stream first: a visibility change during a pending
      // reconnect could otherwise leave two EventSources open at once, only
      // one of which is ever cleaned up.
      es?.close();
      const url = `${streamUrl}?access_token=${encodeURIComponent(token)}`;
      es = new EventSource(url);

      es.onopen = () => {
        if (cancelledRef.current) return;
        setStreaming(true);
        stopPolling();
        startWatchdog();
      };
      es.onmessage = (ev) => {
        if (cancelledRef.current) return;
        // Counts as liveness even if the payload turns out to be malformed:
        // the connection is demonstrably still delivering.
        lastFrameAt = Date.now();
        try {
          const parsed = JSON.parse(ev.data) as T;
          setData(parsed);
          setLastUpdatedAt(Date.now());
          setError(null);
        } catch {
          // Ignore malformed frames; keep last good data.
        }
      };
      es.onerror = () => failStream("error");
    };

    const onVis = () => {
      if (!pauseWhenHidden) return;
      if (document.hidden) {
        stopWatchdog();
        es?.close();
        es = null;
        stopPolling();
        setStreaming(false);
      } else {
        startStream();
      }
    };

    startStream();
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelledRef.current = true;
      document.removeEventListener("visibilitychange", onVis);
      if (reconnectId != null) window.clearTimeout(reconnectId);
      stopWatchdog();
      stopPolling();
      es?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, lastUpdatedAt, streaming };
}
